import 'react-datepicker/dist/react-datepicker.css';

import useRequest from '@ahooksjs/use-request';
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import {
  generalErrorHandler,
  getRegionByStockCode,
  indexSearchStringMapper,
} from '../../actions/utils';
import { tipsConstants } from '../../constants';
import useDispatch from '../../hooks/useDispatch';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  Category,
  StockLookupEntity,
} from '../../py-generated/datalouder/stock/types';
import { tipsService } from '../../services';
import { StockSuggestionItem } from '../../shared/types';
import CreateOrEditTipsForm from './createOrEditForm/CreateOrEditTipsForm';

const preprocessStockCodeIfStockIsIndex = (stockCode: string) => {
  const foundIndex = indexSearchStringMapper.find(
    item => item.searchString === stockCode
  );
  const finalStockCode = foundIndex ? foundIndex.stockCode : stockCode;
  const region = foundIndex
    ? foundIndex.region
    : getRegionByStockCode(finalStockCode);

  return {
    stockCode: finalStockCode,
    region,
  };
};

export type Direction = 'bullish' | 'bearish';
export interface InvalidTracker {
  influencer: boolean;
  stockCode: boolean;
  direction: boolean;
  predictionDate: boolean;
  channel: boolean;
  sourceLink: boolean;
  keyComment: boolean;
}

const validator = (
  currentInvalid: InvalidTracker,
  dataToTrack: {
    influencer: string;
    isStockCodeFromSuggestion: boolean;
    direction: Direction | undefined;
    stockPrice: number | undefined;
    stockCode: string;
    channel: string | undefined;
    sourceLink: string | undefined;
    date: Date;
  },
  setInvalid: (newInvalid: InvalidTracker) => void
): boolean => {
  const {
    influencer,
    isStockCodeFromSuggestion,
    direction,
    stockCode,
    channel,
    sourceLink,
    date,
  } = dataToTrack;
  const newInvalid = { ...currentInvalid };
  newInvalid.influencer = influencer.length === 0;
  newInvalid.stockCode = !isStockCodeFromSuggestion || stockCode.length <= 0;
  newInvalid.direction = direction === undefined;
  newInvalid.channel =
    (!channel || channel.length === 0) &&
    (!sourceLink || sourceLink.length === 0);
  newInvalid.sourceLink =
    (!channel || channel.length === 0) &&
    (!sourceLink || sourceLink.length === 0);
  newInvalid.predictionDate = date > new Date();
  setInvalid(newInvalid);

  const checkValuesInvalid = Object.values(newInvalid);
  return checkValuesInvalid.every(val => val === false);
};

const initialInvalidTracker = {
  influencer: false,
  stockCode: false,
  direction: false,
  predictionDate: false,
  channel: false,
  sourceLink: false,
  keyComment: false,
};

export interface CurEditTipsData {
  tipsId: string;
  influencer: string;
  stockCode: string;
  direction: Direction;
  date: Date;
  channel?: string;
  sourceLink?: string;
  keyComment?: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSubmit: () => void;
  initialValues?: CurEditTipsData;
  createWithStock?: StockLookupEntity;
}

const legacyProcessSearchPrice = priceData => {
  // as we deprecate the backend searchPrice API,
  // some of the logics are moved here:
  const v1Result = { ...priceData };
  if (priceData.date) {
    // our old system needs a priceDate field, in format of
    // "2022-03-22 00:00:00"
    const priceMinute = priceData.minute
      ? priceData.minute.slice(0, 2) +
        ':' +
        priceData.minute.slice(2, 4) +
        ':00'
      : '00:00:00';
    const priceDate =
      priceData.date.slice(0, 4) +
      '-' +
      priceData.date.slice(4, 6) +
      '-' +
      priceData.date.slice(6) +
      ' ' +
      priceMinute;
    v1Result['priceDate'] = priceDate;
  }
  if (priceData.price) v1Result['stockPrice'] = priceData.price;
  return v1Result;
};

const CreateOrEditTipsModal: React.FC<Props> = ({
  show,
  onHide,
  onSubmit,
  initialValues,
  createWithStock,
}) => {
  const [t] = useTranslation();

  // --------- form ---------
  // influencer
  const [influencer, setInfluencer] = useState<string>('');

  // stockCode
  const [stockCode, setStockCode] = useState<string>('');
  const [stock, setStock] = useState<StockSuggestionItem>();

  // direction
  const [direction, setDirection] = useState<Direction | undefined>();

  // price
  const {
    data: priceData,
    run: searchPrice,
    mutate: setPriceData,
    loading: loadingPointInTimePrice,
  } = useRequest(tipsService.queryPointInTimePriceOptions, {
    manual: true,
    debounceInterval: 200,
  });
  const { stockPrice, priceDate, openPrice, needRefresh } =
    legacyProcessSearchPrice(priceData || {}) || {};

  // prediction date
  const now = new Date();
  const today = now.toLocaleDateString();
  const [date, setDate] = useState<Date>(now);
  const isToday = date ? date.toLocaleDateString() === today : false;

  // channel
  const [channel, setChannel] = useState<string | undefined>();

  // source link
  const [sourceLink, setSourceLink] = useState<string | undefined>();

  // key comments
  const [keyComment, setKeyComment] = useState<string | undefined>();
  // -------------------------

  // other helpers
  const [isStockCodeFromSuggestion, setIsStockCodeFromSuggestion] =
    useState<boolean>(false);

  const [loading, setLoading] = useState(false);

  const [invalid, setInvalid] = useState<InvalidTracker>(initialInvalidTracker);
  const dispatch = useDispatch();

  const clearForm = useCallback(() => {
    dispatch({ type: tipsConstants.TIPS_RESET });

    setInfluencer('');
    setStockCode('');
    setStock(null);
    setDate(new Date());
    setChannel('');
    setSourceLink('');
    setKeyComment('');
    setPriceData(null);
  }, [dispatch, setPriceData]);

  // -------------- handle edit tips --------------
  const setInitialValues = useCallback(() => {
    if (initialValues) {
      setInfluencer(initialValues.influencer);
      setDirection(initialValues.direction);
      setStockCode(initialValues.stockCode);
      setDate(initialValues.date);
      setChannel(initialValues.channel);
      setSourceLink(initialValues.sourceLink);
      setKeyComment(initialValues.keyComment);
      setIsStockCodeFromSuggestion(true);
    }
  }, [initialValues]);

  useEffect(() => {
    // override states for edit
    setInitialValues();
  }, [setInitialValues]);
  // ----------------------------------------------

  // ---------- handle create from lookup ----------
  useEffect(() => {
    if (createWithStock) {
      setStockCode(createWithStock.stockCode);
      setStock({
        stockCode: createWithStock.stockCode,
        stockType:
          createWithStock['category'] === 'CRPT'
            ? 'crypto'
            : createWithStock['category'].toLocaleLowerCase(),
        name_en: createWithStock.name_en,
        name_zh: createWithStock.name_zh,
      });
      setIsStockCodeFromSuggestion(true);
    }
  }, [createWithStock]);

  // ----------------------------------------------

  const handleHideModal = useCallback(() => {
    onHide();
    clearForm();
    setInvalid(initialInvalidTracker);
  }, [onHide, clearForm]);

  useEffect(() => {
    if (show && stockCode !== '' && date && isStockCodeFromSuggestion) {
      const { stockCode: finalStockCode, region } =
        preprocessStockCodeIfStockIsIndex(stockCode);
      const hkDate = new Date(
        date.getTime() - (date.getTimezoneOffset() + 8 * 60) * 60_000
      );
      // TODO: remove this ugly category workaround
      // once we have category every where.
      let category = stock
        ? stock.stockType.toUpperCase()
        : region.toUpperCase();
      if (category === 'OTC') category = 'US';
      if (category === 'CRYPTO') category = 'CRPT';
      if (category === 'CRPTBN') category = 'CRPTBN';
      searchPrice(finalStockCode, hkDate, category as Category);
    }
  }, [
    dispatch,
    stockCode,
    date,
    isStockCodeFromSuggestion,
    searchPrice,
    stock,
    show,
  ]);

  const createOrEditTip = async (tipsId?: string) => {
    const { stockCode: finalStockCode, region } =
      preprocessStockCodeIfStockIsIndex(stockCode);
    try {
      setLoading(true);
      await tipsService.createOrEditTips(
        region,
        influencer,
        finalStockCode,
        direction,
        stockPrice,
        date,
        channel,
        sourceLink,
        keyComment,
        priceDate,
        openPrice,
        tipsId,
        needRefresh
      );
      onSubmit();
      handleHideModal();
    } catch (error) {
      generalErrorHandler(dispatch)(error);
    } finally {
      setLoading(false);
    }
  };

  const onFormSubmit = e => {
    e.preventDefault();

    const isFormValid = validator(
      invalid,
      {
        influencer,
        isStockCodeFromSuggestion,
        direction,
        stockPrice,
        stockCode,
        channel,
        sourceLink,
        date,
      },
      setInvalid
    );

    if (isFormValid) {
      // if there is initialValues, it's an edit, else it is create
      initialValues ? createOrEditTip(initialValues.tipsId) : createOrEditTip();
    }
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Modal
      show={show}
      centered
      onHide={handleHideModal}
      onShow={() => {
        setDate(now);
      }}
    >
      <Modal.Header
        closeButton
        style={{
          alignItems: 'center',
          padding: isMobile ? '1rem 1rem 0.5rem 1rem' : '1rem',
        }}
      >
        <Modal.Title
          style={isMobile ? { fontSize: 18, padding: 0, marginTop: 0 } : {}}
        >
          {initialValues
            ? t('edit-tips-modal-title')
            : t('create-tips-modal-title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <CreateOrEditTipsForm
          onFormSubmit={onFormSubmit}
          submitDisabled={loading}
          onReset={initialValues ? setInitialValues : clearForm}
          influencer={influencer}
          setInfluencer={setInfluencer}
          stockCode={stockCode}
          setStockCode={setStockCode}
          setStock={setStock}
          isStockCodeFromSuggestion={isStockCodeFromSuggestion}
          setIsStockCodeFromSuggestion={setIsStockCodeFromSuggestion}
          stockPrice={stockPrice}
          direction={direction}
          setDirection={setDirection}
          date={date}
          setDate={setDate}
          isToday={isToday}
          now={now}
          sourceLink={sourceLink}
          setSourceLink={setSourceLink}
          channel={channel}
          setChannel={setChannel}
          keyComment={keyComment}
          setKeyComment={setKeyComment}
          invalid={invalid}
          loadingPointInTimePrice={loadingPointInTimePrice}
        />
      </Modal.Body>
    </Modal>
  );
};

export default CreateOrEditTipsModal;
